import { DB_TYPE } from '@/common/define/common.define';
import {
  getGetV7SqlserverSqlTextQueryKey,
  getV7SqlserverSqlText,
} from '@/api-client/sqlserver/queries/sql-sqlserver-v7-controller/sql-sqlserver-v7-controller';
import { FRAME_NAMES } from '@/common/define/apiTrace.define';
import { formatTimeRange } from '@/database/utils/format.utils';
import { DATABASE_DETAIL_TYPE, DOCUMENT_TEXT_TYPE } from '@/database/adapter/openDetail.define';
import type {
  DocumentTextParams,
  ParameterDetailParams,
  SessionDetailParams,
  SqlDetailParams,
} from '../types';
import { formatName, mergeDetailInfo, parseSchema } from './utils';
import { getRequiredMap } from '@/common/utils/core/detailFieldMeta.utils';
import {
  sessionDetailSchema,
  sqlDetailSchema,
  parameterDetailSchema,
  documentTextSchema,
  type SessionDetailSchema,
  type SqlServerSessionDetailAddition,
  type SqlDetailSchema,
  type SqlServerSqlDetailAddition,
  type ParameterDetailSchema,
  type SqlServerParameterDetailAddition,
  type DocumentTextSchema,
} from './sqlserver.schema';

export const getSqlServerSessionDetailAddition = ({
  detailInfo,
  fromTime,
  toTime,
  isOpenedFromPA,
  init,
  instanceId,
}: SessionDetailParams): { name: string; addition: SqlServerSessionDetailAddition } => {
  const { data } = parseSchema<SessionDetailSchema>(
    sessionDetailSchema,
    mergeDetailInfo(detailInfo, instanceId, { fromTime, toTime, isOpenedFromPA, init }),
    DATABASE_DETAIL_TYPE.SESSION,
  );

  return {
    name: formatName(data.sessionId),
    addition: {
      dbType: DB_TYPE.SQLSERVER,
      instanceId: data.instanceId,
      fromTime: data.fromTime,
      toTime: data.toTime,
      collectTime: data.collectTime,
      sessionId: data.sessionId,
      isOpenedFromPA: data.isOpenedFromPA,
      init: data.init,
    },
  };
};

export const getSqlServerSqlDetailAddition = ({
  detailInfo,
  fromTime,
  toTime,
  instanceId,
}: SqlDetailParams): { name: string; addition: SqlServerSqlDetailAddition } => {
  const { data } = parseSchema<SqlDetailSchema>(
    sqlDetailSchema,
    mergeDetailInfo(detailInfo, instanceId, { fromTime, toTime }),
    DATABASE_DETAIL_TYPE.SQL,
  );

  return {
    name: formatName(data.sqlId),
    addition: {
      dbType: DB_TYPE.SQLSERVER,
      instanceId: data.instanceId,
      fromTime: data.fromTime,
      toTime: data.toTime,
      databaseName: data.databaseName,
      objectName: data.objectName,
      sqlHash: data.sqlHash,
      sqlHandle: data.sqlHandle,
      statementStartOffset: data.statementStartOffset,
      statementEndOffset: data.statementEndOffset,
      planHash: data.planHash,
      planHandle: data.planHandle,
      sqlId: data.sqlId,
      sqlTextHashId: data.sqlTextHashId,
      sessionId: data.sessionId,
    },
  };
};

export const getSqlServerParameterDetailAddition = ({
  detailInfo,
  fromTime,
  toTime,
  instanceId,
}: ParameterDetailParams): { name: string; addition: SqlServerParameterDetailAddition } => {
  const { data } = parseSchema<ParameterDetailSchema>(
    parameterDetailSchema,
    mergeDetailInfo(detailInfo, instanceId, { fromTime, toTime }),
    DATABASE_DETAIL_TYPE.PARAMETER,
  );

  return {
    name: formatName(data.parameterName),
    addition: {
      dbType: DB_TYPE.SQLSERVER,
      instanceId: data.instanceId,
      fromTime: data.fromTime,
      toTime: data.toTime,
    },
  };
};

export const getSqlServerDocumentTextParams = ({
  detailInfo,
  fromTime,
  toTime,
  instanceId,
  period,
  useFieldValidation,
}: DocumentTextParams) => {
  const formattedTime = formatTimeRange({ fromTime, toTime });
  const timeInfo = period ? { period } : formattedTime;

  if (!useFieldValidation) {
    return {
      instanceId,
      ...timeInfo,
      sqlId: detailInfo.sqlId,
      sqlTextHashId: detailInfo.sqlTextHashId,
    };
  }

  const { data } = parseSchema<DocumentTextSchema>(
    documentTextSchema,
    mergeDetailInfo(detailInfo, instanceId),
    DOCUMENT_TEXT_TYPE,
  );

  return {
    instanceId: data.instanceId,
    ...timeInfo,
    sqlId: data.sqlId,
    sqlTextHashId: data.sqlTextHashId,
  };
};

export const fetchSqlServerDocumentText = async ({
  frameName,
  abortController,
  ...rest
}: DocumentTextParams) => {
  const params = getSqlServerDocumentTextParams({ ...rest, useFieldValidation: true });

  const {
    data: { data = [] },
  } = await getV7SqlserverSqlText(
    params,
    { params: { frameName: frameName || FRAME_NAMES.DOCUMENT_WINDOW.FULL_TEXT } },
    abortController?.signal,
  );

  return data[0]?.sqlText || null;
};

export const getSqlServerDocumentTextQueryKey = (params: DocumentTextParams | null) => {
  if (!params) {
    return getGetV7SqlserverSqlTextQueryKey();
  }

  const convertedParams = getSqlServerDocumentTextParams({ ...params, useFieldValidation: false });

  return getGetV7SqlserverSqlTextQueryKey(convertedParams);
};

export const sqlServerSqlDetailRequiredMap = getRequiredMap(sqlDetailSchema);
export const sqlServerSessionDetailRequiredMap = getRequiredMap(sessionDetailSchema);
export const sqlServerDocumentTextRequiredMap = getRequiredMap(documentTextSchema);
